import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import FreeYearActivationModal from './FreeYearActivationModal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { CalendarCheck, CreditCard, Gift } from 'lucide-react';
import { toast } from 'react-toastify';
import ParticleService from '../ParticleService';
import { normalizeHelcimPaySuccess } from '../helcimPayResponse';
import { billingIntervalLabel, formatSubscriptionDate as formatDate, subscriptionStatusLabel as statusLabel } from '../subscriptionPresentation';

const variant = status => {
  if (['Active', 'FreePeriod'].includes(status)) return 'success';
  if (['ActivationRequired', 'Pending', 'GracePeriod', 'PastDue'].includes(status)) return 'warning';
  if (['Restricted', 'Suspended', 'Expired', 'Cancelled'].includes(status)) return 'danger';
  return 'secondary';
};

const AccessPeriod = ({ device }) => {
  if (device.canActivateFreePeriod) return (
    <>
      <div>Activate by <strong>{formatDate(device.activationDeadlineUtc)}</strong></div>
      {device.activationWillBeBackdated && <div className="small text-warning">The activation window has passed; the start date will be backdated.</div>}
    </>
  );
  if (device.status === 'Pending') return (
    <>
      <div>Starts <strong>{formatDate(device.startsAtUtc)}</strong></div>
      {device.endsAtUtc && <div className="small text-muted">Scheduled through {formatDate(device.endsAtUtc)}</div>}
    </>
  );
  if (['Restricted', 'Suspended', 'Expired', 'Cancelled'].includes(device.status)) return (
    <div>Ended <strong>{formatDate(device.endsAtUtc)}</strong></div>
  );
  if (['GracePeriod', 'PastDue'].includes(device.status)) return (
    <>
      <div>Regular access ended <strong>{formatDate(device.endsAtUtc)}</strong></div>
      {device.graceEndsAtUtc && <div className="small text-warning">Temporary access through {formatDate(device.graceEndsAtUtc)}</div>}
    </>
  );
  return <div>{device.endsAtUtc ? <>Available through <strong>{formatDate(device.endsAtUtc)}</strong></> : 'No end date recorded'}</div>;
};

const loadHelcimPay = () => new Promise((resolve, reject) => {
  if (window.appendHelcimPayIframe) { resolve(); return; }
  const existing = document.getElementById('helcim-pay-script');
  if (existing) {
    existing.addEventListener('load', resolve, { once: true });
    existing.addEventListener('error', () => reject(new Error('Could not load HelcimPay.')), { once: true });
    return;
  }
  const script = document.createElement('script');
  script.id = 'helcim-pay-script';
  script.src = 'https://secure.helcim.app/helcim-pay/services/start.js';
  script.async = true;
  script.onload = resolve;
  script.onerror = () => reject(new Error('Could not load HelcimPay.'));
  document.body.appendChild(script);
});

export default function CustomerSubscriptionStatus() {
  const [summary, setSummary] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDevice, setPendingDevice] = useState(null);
  const [checkoutStarting, setCheckoutStarting] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const checkoutRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [result, offerResult] = await Promise.all([
      ParticleService.getMySubscriptionStatus(),
      ParticleService.getCheckoutOffers(),
    ]);
    if (result.error) toast.error(result.detail || result.error); else setSummary(result);
    if (offerResult.error) toast.error(offerResult.detail || offerResult.error); else setOffers(offerResult);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const eligibleDevices = summary?.devices?.filter(device => device.canStartPaidSubscription) || [];
    if (eligibleDevices.length === 1 && !selectedDeviceId) setSelectedDeviceId(eligibleDevices[0].deviceId);
  }, [summary, selectedDeviceId]);

  useEffect(() => {
    const receiveHelcimMessage = async event => {
      const checkout = checkoutRef.current;
      if (!checkout || event.origin !== 'https://secure.helcim.app' ||
          event.data?.eventName !== `helcim-pay-js-${checkout.checkoutToken}`) return;
      if (event.data.eventStatus === 'ABORTED') {
        checkoutRef.current = null;
        toast.error(typeof event.data.eventMessage === 'string'
          ? event.data.eventMessage
          : 'Payment method verification was cancelled or declined.');
      } else if (event.data.eventStatus === 'SUCCESS') {
        checkoutRef.current = null;
        const response = normalizeHelcimPaySuccess(event.data.eventMessage);
        if (!response) {
          toast.error('HelcimPay returned an invalid response.');
          return;
        }
        const result = await ParticleService.completeHelcimCheckout(checkout.sessionId, response);
        if (result.error) toast.error(result.detail || result.error);
        else {
          toast.success(result.subscriptionStatus === 'Pending'
            ? 'Recurring subscription scheduled after the current access period.'
            : 'Recurring subscription created successfully.');
          await load();
        }
      } else if (event.data.eventStatus === 'HIDE') checkoutRef.current = null;
    };
    window.addEventListener('message', receiveHelcimMessage);
    return () => window.removeEventListener('message', receiveHelcimMessage);
  }, [load]);

  const startCheckout = async offer => {
    if (checkoutStarting) return;
    if (!selectedDeviceId) {
      toast.error('Select the device this subscription will cover.');
      return;
    }
    const offerKey = `${offer.currency}-${offer.billingInterval}`;
    setCheckoutStarting(offerKey);
    const result = await ParticleService.startHelcimCheckout(offer.currency, offer.billingInterval, selectedDeviceId);
    if (result.error) toast.error(result.detail || result.error);
    else {
      try {
        await loadHelcimPay();
        checkoutRef.current = result;
        window.appendHelcimPayIframe(result.checkoutToken, true);
      } catch (error) {
        toast.error(error.message);
      }
    }
    setCheckoutStarting(null);
  };

  return (
    <>
      <Card className="shadow-sm m-3 mb-4">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
          <strong className="d-flex align-items-center gap-2"><CalendarCheck size={20} /> Your dashboard access</strong>
          {summary && <Badge bg={variant(summary.status)}>{statusLabel(summary.status)}</Badge>}
        </Card.Header>
        <Card.Body className={loading ? 'text-center py-4' : 'p-0'}>
          {loading ? <Spinner animation="border" size="sm" /> : !summary?.devices?.length ? (
            <p className="text-muted p-3 mb-0">No devices are currently assigned to this account.</p>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light"><tr><th>Device</th><th>Access now</th><th>Current access period</th><th>Paid renewal</th><th></th></tr></thead>
              <tbody>{summary.devices.map(device => (
                <tr key={device.deviceId}>
                  <td><strong>{device.displayName || 'Unnamed device'}</strong><div className="small text-muted font-monospace">{device.particleDeviceId}</div></td>
                  <td><Badge bg={variant(device.status)}>{statusLabel(device.status)}</Badge><div className="small text-muted mt-1">{device.entitlementLabel || 'One included free year is available'}</div></td>
                  <td><AccessPeriod device={device} /></td>
                  <td>{device.paidPlan ? (
                    <div>
                      <Badge bg={device.paidPlan.status === 'Scheduled' ? 'primary' : variant(device.paidPlan.status)}>
                        {device.paidPlan.status === 'Scheduled' ? 'Renewal scheduled' : statusLabel(device.paidPlan.status)}
                      </Badge>
                      <div className="small mt-1"><strong>{device.paidPlan.currency} {billingIntervalLabel(device.paidPlan.billingInterval)}</strong></div>
                      {device.paidPlan.status === 'Scheduled' && <div className="small text-muted">First payment {formatDate(device.paidPlan.startsAtUtc)}</div>}
                      {device.paidPlan.status !== 'Scheduled' && device.paidPlan.endsAtUtc && <div className="small text-muted">Current paid period ends {formatDate(device.paidPlan.endsAtUtc)}</div>}
                      {device.paidPlan.cancelAtPeriodEnd && <div className="small text-warning">Does not renew automatically</div>}
                    </div>
                  ) : <><span className="text-muted">Not scheduled</span><div className="small text-muted">Choose a plan below to continue access.</div></>}</td>
                  <td className="text-end">{device.canActivateFreePeriod && <Button size="sm" onClick={() => setPendingDevice(device)}><Gift size={15} className="me-1" /> Start my free year</Button>}</td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm m-3 mb-4">
        <Card.Header className="bg-white"><strong className="d-flex align-items-center gap-2"><CreditCard size={20} /> Renewal options</strong></Card.Header>
        <Card.Body>
          {summary?.devices?.length > 0 && summary.devices.every(device => !device.canStartPaidSubscription) ? (
            <Alert variant="success" className="mb-0"><strong>Your paid renewal is already set up.</strong><div className="small mt-1">You do not need to subscribe again. The scheduled date is shown beside each device above.</div></Alert>
          ) : (
            <>
              <Alert variant="secondary" className="small"><strong>Keep your dashboard access active.</strong><div className="mt-1">Choose a device and plan below. Helcim securely saves your card; Cannon Control never receives your full card number. If the device has access today, the first payment is scheduled for after that access ends.</div></Alert>
              <Form.Group className="mb-3" controlId="subscription-device">
                <Form.Label>Device covered by this subscription</Form.Label>
                <Form.Select value={selectedDeviceId} onChange={event => setSelectedDeviceId(event.target.value)}>
                  <option value="">Select a device…</option>
                  {(summary?.devices || []).map(device => (
                    <option key={device.deviceId} value={device.deviceId} disabled={!device.canStartPaidSubscription}>
                      {device.displayName || device.particleDeviceId} — {device.canStartPaidSubscription
                        ? statusLabel(device.status)
                        : 'Paid subscription already configured'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <div className="row g-3">
                {offers.map(offer => <div className="col-sm-6 col-xl-3" key={`${offer.currency}-${offer.billingInterval}`}>
                  <div className="border rounded p-3 h-100 d-flex flex-column">
                    <strong>{offer.currency} {offer.billingInterval.toLowerCase()}</strong>
                    <div className="fs-4 mt-2">{new Intl.NumberFormat('en-CA', { style: 'currency', currency: offer.currency }).format(offer.amount)}</div>
                    <div className="small text-muted mb-2">per device</div>
                    {offer.discountApplied && <div className="small text-success mb-2">Customer discount applied</div>}
                    {!offer.available && <div className="small text-muted mb-2">{offer.unavailableReason}</div>}
                    <Button className="mt-auto" size="sm" disabled={!offer.available || !selectedDeviceId || checkoutStarting === `${offer.currency}-${offer.billingInterval}`} onClick={() => startCheckout(offer)}>
                      {checkoutStarting === `${offer.currency}-${offer.billingInterval}` ? 'Opening…' : 'Subscribe with Helcim'}
                    </Button>
                  </div>
                </div>)}
                {offers.length === 0 && <div className="col-12"><Alert variant="secondary" className="mb-0">No online payment plans are currently available. Please contact Margo Supplies for renewal assistance.</Alert></div>}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <FreeYearActivationModal device={pendingDevice} onHide={() => setPendingDevice(null)} onActivated={load} />
    </>
  );
}
